import { InsightCardResponse, MetricSummary } from '@@types/index';
import { InsightDesignTokens, RankKey } from './insightDesignTokens';
import kioLogoSrc from '@resources/image/kioLogo.png';
import goodCharacterSrc from '@resources/image/donation/good.webp';

const SIZE = InsightDesignTokens.png.size;
const KO_FONT_STACK = '-apple-system, "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", system-ui, sans-serif';

let cachedLogo: HTMLImageElement | null = null;
let cachedLogoFailed = false;

function loadLogo(): Promise<HTMLImageElement | null> {
  if (cachedLogo) return Promise.resolve(cachedLogo);
  if (cachedLogoFailed) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      cachedLogo = img;
      resolve(img);
    };
    img.onerror = () => {
      cachedLogoFailed = true;
      resolve(null);
    };
    img.src = kioLogoSrc;
  });
}

let cachedGood: HTMLImageElement | null = null;
let cachedGoodFailed = false;

function loadGood(): Promise<HTMLImageElement | null> {
  if (cachedGood) return Promise.resolve(cachedGood);
  if (cachedGoodFailed) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      cachedGood = img;
      resolve(img);
    };
    img.onerror = () => {
      cachedGoodFailed = true;
      resolve(null);
    };
    img.src = goodCharacterSrc;
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number) {
  const words = text.split(' ');
  let line = '';
  let curY = y;
  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (ctx.measureText(next).width > maxWidth && line) {
      ctx.fillText(line, x, curY);
      line = w;
      curY += lineHeight;
    } else {
      line = next;
    }
  }
  ctx.fillText(line, x, curY);
}

function drawBackground(ctx: CanvasRenderingContext2D) {
  // Subtle cream diagonal — 살짝 따뜻한 tone, 그라데이션은 자연스럽게.
  const grad = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  grad.addColorStop(0, '#FFF8EB');
  grad.addColorStop(1, '#FFEAD0');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, SIZE, SIZE);
}

function drawGoodMedal(ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, good: HTMLImageElement | null) {
  if (good) {
    const size = radius * 2.4;
    ctx.drawImage(good, centerX - size / 2, centerY - size / 2, size, size);
    return;
  }

  // fallback — 캐릭터 로드 실패 시 옅은 KIO_ORANGE 원으로 메달 자리만 표시
  ctx.save();
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fillStyle = InsightDesignTokens.brand.faint;
  ctx.fill();
  ctx.restore();
}

function drawHeader(ctx: CanvasRenderingContext2D, card: InsightCardResponse, workspaceName: string, good: HTMLImageElement | null) {
  ctx.textAlign = 'left';

  // 1. 주점 이름 (상단 좌측)
  ctx.fillStyle = InsightDesignTokens.brand.dark;
  ctx.font = `700 34px ${KO_FONT_STACK}`;
  ctx.fillText(workspaceName, 80, 90);

  // 2. FESTIVAL DAILY · 날짜
  ctx.fillStyle = InsightDesignTokens.brand.dark;
  ctx.globalAlpha = 0.6;
  ctx.font = `500 22px ${KO_FONT_STACK}`;
  ctx.fillText(`FESTIVAL DAILY · ${card.referenceDate}`, 80, 124);
  ctx.globalAlpha = 1;

  // 3. good.png 캐릭터 (메달 자리) + 헤드라인
  const medalRadius = 64;
  const medalX = 80 + medalRadius;
  const medalY = 218;
  drawGoodMedal(ctx, medalX, medalY, medalRadius, good);

  // 4. 헤드라인 (메달 오른쪽, 메달 vertical 중앙 정렬)
  ctx.fillStyle = InsightDesignTokens.brand.dark;
  ctx.font = `900 64px ${KO_FONT_STACK}`;
  ctx.textAlign = 'left';
  const headlineX = medalX + medalRadius + 28;
  wrapText(ctx, card.headline, headlineX, medalY + 22, SIZE - headlineX - 80, 74);
}

const CELL_ALPHA_TOP: Record<RankKey, number> = { 1: 0.92, 2: 0.8, 3: 0.65, 4: 0.5 };
const CELL_ALPHA_BOTTOM: Record<RankKey, number> = { 1: 0.6, 2: 0.45, 3: 0.32, 4: 0.22 };

function drawGlassCell(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, rank: RankKey) {
  const radius = 20;

  // 1. soft drop shadow
  ctx.save();
  ctx.shadowColor = 'rgba(180, 110, 50, 0.12)';
  ctx.shadowBlur = 24;
  ctx.shadowOffsetY = 6;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.001)'; // invisible draw — just to cast shadow
  roundRect(ctx, x, y, w, h, radius);
  ctx.fill();
  ctx.restore();

  // 2. glassmorphic fill: vertical white→white-faint gradient
  const grad = ctx.createLinearGradient(x, y, x, y + h);
  grad.addColorStop(0, `rgba(255, 255, 255, ${CELL_ALPHA_TOP[rank]})`);
  grad.addColorStop(1, `rgba(255, 255, 255, ${CELL_ALPHA_BOTTOM[rank]})`);
  ctx.fillStyle = grad;
  roundRect(ctx, x, y, w, h, radius);
  ctx.fill();

  // 3. inner highlight stroke (top edge brighter)
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
  ctx.lineWidth = 1.5;
  roundRect(ctx, x + 0.75, y + 0.75, w - 1.5, h - 1.5, radius - 0.75);
  ctx.stroke();
}

function drawGrid(ctx: CanvasRenderingContext2D, top: MetricSummary[]) {
  const startY = 320;
  const cellW = (SIZE - 80 * 2 - 24) / 2;
  const cellH = 250;
  const gap = 20;
  // 셀 내부 위계 — top/bottom padding 대칭으로 (note가 bottom과 멀어보이지 않게)
  // padTop 32, padBottom 32
  const padX = 28;
  const labelY = 56; // top padding ~32 + caption baseline
  const valueY = 150; // 중앙 영역 (hero)
  const noteY = 218; // bottom padding ~32 (cellH 250 - 218 = 32)

  top.slice(0, 4).forEach((m, idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    const x = 80 + col * (cellW + gap);
    const y = startY + row * (cellH + gap);

    const rankKey = (idx + 1) as RankKey;

    drawGlassCell(ctx, x, y, cellW, cellH, rankKey);

    const tone = InsightDesignTokens.rank[rankKey];

    // label (caption, top)
    ctx.fillStyle = tone.labelText;
    ctx.font = `600 26px ${KO_FONT_STACK}`;
    ctx.textAlign = 'left';
    ctx.fillText(`#${rankKey}  ${m.label}`, x + padX, y + labelY);

    // value (hero, center)
    ctx.fillStyle = tone.text;
    ctx.font = `900 78px ${KO_FONT_STACK}`;
    ctx.fillText(m.value, x + padX, y + valueY);

    // note (caption, bottom) — label과 동일 위계
    const note = m.percentile != null ? `상위 ${Math.round(100 - m.percentile)}%` : m.milestoneStep != null ? '마일스톤 돌파' : null;
    if (note) {
      ctx.fillStyle = tone.labelText;
      ctx.font = `500 22px ${KO_FONT_STACK}`;
      ctx.fillText(note, x + padX, y + noteY);
    }
  });
}

async function drawFooter(ctx: CanvasRenderingContext2D) {
  const logo = await loadLogo();
  const footerY = SIZE - 90;

  if (logo) {
    const logoW = 130;
    const logoH = (logo.height / logo.width) * logoW;
    const x = (SIZE - logoW) / 2;
    ctx.drawImage(logo, x, footerY - logoH / 2, logoW, logoH);
  } else {
    ctx.fillStyle = InsightDesignTokens.brand.dark;
    ctx.font = `800 40px ${KO_FONT_STACK}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('KIO', SIZE / 2, footerY);
    ctx.textBaseline = 'alphabetic';
  }
}

export async function renderInsightCardToCanvas(card: InsightCardResponse, workspaceName: string): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const good = await loadGood();

  drawBackground(ctx);
  drawHeader(ctx, card, workspaceName, good);
  drawGrid(ctx, card.topMetrics);
  await drawFooter(ctx);

  return canvas;
}

export async function renderInsightCardToBlob(card: InsightCardResponse, workspaceName: string): Promise<Blob> {
  const canvas = await renderInsightCardToCanvas(card, workspaceName);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('canvas.toBlob returned null'));
    }, 'image/png');
  });
}

export async function renderInsightCardToDataUrl(card: InsightCardResponse, workspaceName: string): Promise<string> {
  const canvas = await renderInsightCardToCanvas(card, workspaceName);
  return canvas.toDataURL('image/png');
}
